/**
 * EcoSealPro Carousel Variant Picker
 * A horizontal carousel implementation showing fixed jar sets
 */

(function() {
  'use strict';
  
  /**
   * In quick-view, the same picker markup can exist on the page AND inside the open quick-view.
   * Always scope DOM queries to the currently open quick-view (if present) to avoid targeting the wrong instance.
   */
  const getScopeRoot = function() {
    return (
      document.querySelector('quick-view-drawer details[open]') ||
      document.querySelector('quick-view-drawer details.menu-opening') ||
      document.querySelector('.quick-view--open') ||
      document.querySelector('.quick-view--opening') ||
      document
    );
  };

  /**
   * IMPORTANT:
   * The theme also uses EcoSeal carousel CSS for other carousels (e.g. sibling products).
   * Those carousels intentionally reuse classes like `.ecoseal-carousel-picker`, `.set-card`, `#carousel-track`.
   * When our JS is loaded (lazy) we must ONLY initialise the EcoSeal picker carousel inside the merged picker.
   */
  const getEcoSealCarouselContainer = function(root) {
    const scope = root || getScopeRoot();
    const merged = scope.querySelector('.ecoseal-merged-picker');
    if (merged) {
      const byId = merged.querySelector('#carousel-picker.ecoseal-carousel-picker');
      if (byId) return byId;
    }
    // Standalone fallback (no merged picker on the page)
    return scope.querySelector('.ecoseal-carousel-picker');
  };

  // DOM Helper utilities
  const DOMHelper = {
    getElement: function(selector, root = null) {
      const scope = root || getScopeRoot();
      return scope.querySelector(selector) || null;
    },
    
    getElements: function(selector, root = null) {
      const scope = root || getScopeRoot();
      return scope.querySelectorAll(selector);
    },
    
    createElement: function(tagName, options = {}) {
      const customizedIs = options.is || (options.attributes && options.attributes.is);
      let element;
      if (customizedIs) {
        try {
          element = document.createElement(tagName, { is: customizedIs });
        } catch (e) {
          element = document.createElement(tagName);
        }
      } else {
        element = document.createElement(tagName);
      }
      
      if (options.className) {
        element.className = options.className;
      }
      
      if (options.attributes) {
        Object.entries(options.attributes).forEach(([key, value]) => {
          if (key === 'is') return;
          element.setAttribute(key, value);
        });
      }
      
      // Best-effort: keep the `is="..."` attribute even if customized built-ins aren't supported.
      if (customizedIs && !element.getAttribute('is')) {
        try { element.setAttribute('is', customizedIs); } catch (e) { /* no-op */ }
      }
      
      if (options.text) {
        element.textContent = options.text;
      }
      
      if (options.html) {
        element.innerHTML = options.html;
      }
      
      if (options.styles) {
        Object.entries(options.styles).forEach(([prop, value]) => {
          element.style[prop] = value;
        });
      }
      
      if (options.children) {
        options.children.forEach(child => {
          element.appendChild(child);
        });
      }
      
      return element;
    }
  };

  // Configuration for fixed sets - SINGLE SOURCE OF TRUTH
  const Config = {
    // Default set configuration
    defaultSetId: 'picknick', // Picknick Set is now default
    
    // SINGLE CONFIG: Add new sets here and everything else works automatically
    // Each set needs: id, sku, defaultName, imageName, badgeType, includes, description, tier
    // The system will auto-generate translations, asset URLs, and all other mappings
    fixedSets: [

      // 4. Spice Set - 74,97€
      {
        id: 'spice',
        sku: '_ECOPRO1SET_SPICE',
        defaultName: 'Spice Set',
        imageName: 'Set_Spice.png',
        badgeType: 'spice_master',
        includes: [
          { type: 'vacuum_sealer', quantity: 1 },
          { type: 'vaku_jar', size: '230ml', quantity: 9 }
        ],
        description: 'Spice storage and organization',
        tier: 'standard',
        popular: false
      },
      // 5. Jam Set - 74,97€
      {
        id: 'jam',
        sku: '_ECOPRO1SET_JAM',
        defaultName: 'Jam Set',
        imageName: 'Set_Jam.png',
        badgeType: 'jam_hero',
        includes: [
          { type: 'vacuum_sealer', quantity: 1 },
          { type: 'vaku_jar', size: '350ml', quantity: 9 }
        ],
        description: 'Jam making and preserving',
        tier: 'standard',
        popular: false
      },
      // 6. MealPrep Set - 85,98€
      {
        id: 'mealprep',
        sku: '_ECOPRO1SET_MEALPREP',
        defaultName: 'MealPrep Set',
        imageName: 'Set_MealPrep.png',
        badgeType: 'mealprep_star',
        includes: [
          { type: 'vacuum_sealer', quantity: 1 },
          { type: 'vaku_jar', size: '230ml', quantity: 3 },
          { type: 'ball_mason', size: '473ml', quantity: 2 },
          { type: 'ball_mason', size: '946ml', quantity: 2 }
        ],
        description: 'Meal prepping and batch cooking',
        tier: 'standard',
        popular: false
      },
      // 7. Picknick Set - 88,18€
      {
        id: 'picknick',
        sku: '_ECOPRO1SET_PICKNICK',
        defaultName: 'Picknick Set',
        imageName: 'Set_Picknick.png',
        badgeType: 'portable_pack',
        includes: [
          { type: 'vacuum_sealer', quantity: 1 },
          { type: 'ball_mason', size: '236ml', quantity: 2 },
          { type: 'ball_mason', size: '473ml', quantity: 2 },
          { type: 'ball_mason', size: '946ml', quantity: 2 }
        ],
        description: 'Outdoor activities and travel',
        tier: 'standard',
        popular: false
      },
      // 8. Family Set - 88,47€
      {
        id: 'family',
        sku: '_ECOPRO1SET_FAMILY',
        defaultName: 'Family Set',
        imageName: 'Set_Family.png',
        badgeType: 'family_favourite',
        includes: [
          { type: 'vacuum_sealer', quantity: 1 },
          { type: 'vaku_jar', size: '350ml', quantity: 3 },
          { type: 'vaku_jar', size: '1062ml', quantity: 3 },
          { type: 'ball_mason', size: '473ml', quantity: 3 }
        ],
        description: 'Families and versatile use',
        tier: 'premium',
        popular: false
      },
      // 9. Discover Set - 106,87€
      {
        id: 'discover',
        sku: '_ECOPRO1SET_DISCOVER',
        defaultName: 'Discover Set',
        imageName: 'Set_Discover.png',
        badgeType: 'discovery_pack',
        includes: [
          { type: 'vacuum_sealer', quantity: 1 },
          { type: 'vaku_jar', size: '230ml', quantity: 3 },
          { type: 'vaku_jar', size: '350ml', quantity: 3 },
          { type: 'vaku_jar', size: '770ml', quantity: 3 },
          { type: 'vaku_jar', size: '1062ml', quantity: 3 },
          { type: 'ball_mason', size: '236ml', quantity: 1 },
          { type: 'ball_mason', size: '473ml', quantity: 1 },
          { type: 'ball_mason', size: '946ml', quantity: 1 }
        ],
        description: 'First-time users and testing all sizes',
        tier: 'top',
        popular: false
      },
      // 10. Kitchen Love Set - 141,04€ - Most expensive
      {
        id: 'kitchenlove',
        sku: '_ECOPRO1SET_KITCHENLOVE',
        defaultName: 'Kitchen Love Set',
        imageName: 'Set_KitchenLove.png',
        badgeType: 'gourmet_selection',
        includes: [
          { type: 'vacuum_sealer', quantity: 1 },
          { type: 'vaku_jar', size: '230ml', quantity: 3 },
          { type: 'vaku_jar', size: '350ml', quantity: 3 },
          { type: 'vaku_jar', size: '770ml', quantity: 3 },
          { type: 'vaku_jar', size: '1062ml', quantity: 3 },
          { type: 'ball_mason', size: '236ml', quantity: 3 },
          { type: 'ball_mason', size: '473ml', quantity: 3 },
          { type: 'ball_mason', size: '946ml', quantity: 3 }
        ],
        description: 'Ultimate kitchen enthusiasts and professionals',
        tier: 'premium',
        popular: false
      }
    ],
    
    // DYNAMIC TRANSLATIONS - Uses single source of truth config to determine what translations to load
    translations: {
      // Get translation with automatic fallback system based on config
      get: function(key, defaultValue = null, setId = null) {
        // Try Shopify translations first
        if (window.shopifyTranslations && window.shopifyTranslations[key] !== undefined) {
          // Decode HTML entities that come from Shopify's | json filter
          const text = window.shopifyTranslations[key];
          const textarea = document.createElement('textarea');
          textarea.innerHTML = text;
          return textarea.value;
        }
        
        // For set names, use config default if translation missing
        if (key.startsWith('set_') && setId) {
          const set = Config.fixedSets.find(s => s.id === setId);
          // This fallback is for set names only (e.g., "set_saver"), not other keys like descriptions.
          if (set && key === `set_${set.id}` && set.defaultName) {
            return set.defaultName;
          }
        }
        
        // For badge types, use config-driven fallbacks
        if (key.startsWith('badge_')) {
          const badgeType = key.slice('badge_'.length);
          const types = getConfiguredBadgeTypes();
          if (types && types.has(badgeType) && badgeFallbackMap[badgeType]) {
            return badgeFallbackMap[badgeType];
          }
        }
        
        // Final fallback
        return defaultValue || key;
      },
      
      // Get set name with automatic fallback
      getSetName: function(set) {
        const translationKey = `set_${set.id}`;
        return this.get(translationKey, set.defaultName, set.id);
      },
      
      // Get set description with automatic fallback
      getSetDescription: function(set) {
        const translationKey = `set_${set.id}_description`;
        return this.get(translationKey, set.description, set.id);
      },
      
      // Build jar description dynamically
      buildJarDescription: function(item) {
        if (item.type === 'vacuum_sealer') {
          return this.get('vacuum_sealer', 'EcoSeal Pro Vacuum Sealer');
        }
        
        let jarType;
        if (item.type === 'vaku_jar') {
          jarType = item.quantity === 1 ? 
            this.get('jar_vaku_jar', 'Vaku-Jar') : 
            this.get('jar_vaku_jar_plural', 'Vaku-Jars');
        } else if (item.type === 'ball_mason') {
          jarType = item.quantity === 1 ? 
            this.get('jar_ball_mason', 'Ball Mason Jar') : 
            this.get('jar_ball_mason_plural', 'Ball Mason Jars');
        }
        
        return `${item.quantity}x ${item.size} ${jarType}`;
      },
      
      // Build includes list for a set
      buildIncludesList: function(includes) {
        return includes.map(item => this.buildJarDescription(item));
      }
    },
    
    // SHOPIFY ASSETS - Dynamic asset URL handling based on config
    assets: {
      // Get set image URL using config-driven approach
      getSetImage: function(set) {
        // Use imageName from config to lookup proper Shopify URL
        const imageName = set.imageName;
        if (!imageName) {
          return '/assets/placeholder.png';
        }
        
        // Look up proper Shopify asset URL from Liquid-generated mapping
        if (window.shopifyAssetBase && window.shopifyAssetBase.setImageUrls && window.shopifyAssetBase.setImageUrls[imageName]) {
          const imageUrl = window.shopifyAssetBase.setImageUrls[imageName];
          return imageUrl;
        }
        
        // No hardcoded fallbacks - if mapping doesn't exist, image needs to be added
        return '/assets/placeholder.png';
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Cached badge fallbacks (same behavior, less per-call work)
  // ---------------------------------------------------------------------------
  const badgeFallbackMap = {
    best_value: 'Best Value',
    most_popular: 'Most Popular',
    recommended: 'Recommended',
    portable: 'Portable',
    complete_variety: 'Complete Variety',
    new: 'New'
  };

  let configuredBadgeTypesSet = null;
  const getConfiguredBadgeTypes = function() {
    if (configuredBadgeTypesSet) return configuredBadgeTypesSet;
    configuredBadgeTypesSet = new Set(
      (Array.isArray(Config.fixedSets) ? Config.fixedSets : [])
        .map((set) => (set && set.badgeType ? String(set.badgeType) : ''))
        .filter(Boolean)
    );
    return configuredBadgeTypesSet;
  };

  // The main carousel picker application
  const EcoSealCarouselPicker = {
    state: {
      selectedSetId: null,
      orderedSets: null,
      skuSoldOutBySku: null
    },

    /**
     * Get the active DOM scope + the *correct* EcoSeal carousel container for that scope.
     * (Prevents collisions with other carousels that share the same styling classes.)
     */
    getContext: function() {
      const root = getScopeRoot();
      const container = getEcoSealCarouselContainer(root);
      return { root, container };
    },
    
    /**
     * Use the availability-ordered sets if present, else fall back to config order.
     */
    getSets: function() {
      return Array.isArray(this.state.orderedSets) && this.state.orderedSets.length
        ? this.state.orderedSets
        : Config.fixedSets;
    },

    /**
     * Find the variant <option> for a SKU inside the active DOM scope.
     * We rely on `disabled` being set by Liquid when a variant is unavailable.
     */
    getOptionForSku: function(sku) {
      try {
        const root = getScopeRoot();
        const select = root.querySelector('select[id^="Option-"][name^="options["]');
        if (!select) return null;
        const safeSku = (sku || '').trim();
        if (!safeSku) return null;
        return select.querySelector(`option[data-variant-sku="${CSS.escape(safeSku)}"]`);
      } catch (_e) {
        return null;
      }
    },

    isSkuSoldOut: function(sku) {
      const safeSku = (sku || '').trim();
      const map = this.state && this.state.skuSoldOutBySku;
      if (safeSku && map && Object.prototype.hasOwnProperty.call(map, safeSku)) {
        return !!map[safeSku];
      }
      const opt = this.getOptionForSku(safeSku);
      return !!(opt && opt.disabled);
    },

    /**
     * Reorder sets so available ones come first (keeping original order),
     * then sold out ones (also keeping original order).
     */
    computeOrderedSets: function() {
      if (!Array.isArray(Config.fixedSets) || Config.fixedSets.length === 0) {
        this.state.skuSoldOutBySku = null;
        return [];
      }

      const available = [];
      const soldOut = [];

      // If we can't find the variant select yet, keep default order (fail-open).
      const root = getScopeRoot();
      const select = root.querySelector('select[id^="Option-"][name^="options["]');
      if (!select) {
        this.state.skuSoldOutBySku = null;
        return Config.fixedSets.slice();
      }

      const soldOutBySku = {};
      const getOptionForSkuInSelect = function(sku) {
        try {
          const safeSku = (sku || '').trim();
          if (!safeSku) return null;
          return select.querySelector(`option[data-variant-sku="${CSS.escape(safeSku)}"]`);
        } catch (_e) {
          return null;
        }
      };

      Config.fixedSets.forEach(set => {
        // Prefer SKU lookup; if not found, treat as available to preserve UX
        const sku = set && set.sku ? String(set.sku).trim() : '';
        const opt = sku ? getOptionForSkuInSelect(sku) : null;
        const isSold = !!(opt && opt.disabled);
        if (sku) soldOutBySku[sku] = isSold;
        if (isSold) {
          soldOut.push(set);
        } else {
          available.push(set);
        }
      });

      this.state.skuSoldOutBySku = soldOutBySku;
      return available.concat(soldOut);
    },

    init: function() {
      const { root, container } = this.getContext();
      if (!container) return;

      // Guard against multiple initializations for the same rendered picker instance
      if (container.dataset.ecosealCarouselInitialized === 'true') {
        return;
      }
      container.dataset.ecosealCarouselInitialized = 'true';

      // Robust guard: if no fixed sets are configured, disable/hide carousel and exit early
      const hasSets = Array.isArray(Config.fixedSets) && Config.fixedSets.length > 0;
      if (!hasSets) {
        container.style.display = 'none';
        return;
      }
      
      // Try to initialize from URL variant param first
      const initializedFromUrl = this.initFromUrl();

      // Compute availability-based ordering once per rendered picker instance
      this.state.orderedSets = this.computeOrderedSets();
      
      // Create the carousel
      this.createCarousel(container);
      
      // Set up event listeners
      this.setupEventListeners(container);
      
      if (initializedFromUrl) {
        // For URL variants, Shopify has already selected the variant,
        // so we just need to update our UI to match - NEVER update variant
        this.updateUI(false);
      } else {
        // Get current variant from theme and check if it's a fixed set
        const currentVariant = this.getCurrentVariantFromTheme();
        if (currentVariant) {
          const matchingSet = Config.fixedSets.find(set => set.sku === currentVariant.sku);
          if (matchingSet) {
            // Current variant is a fixed set, just select it visually
            this.selectSet(matchingSet, true); // true = skip variant update
          } else {
            // Current variant is not a fixed set, select default but don't update variant
            const defaultSet = this.getDefaultSet();
            if (defaultSet) {
              this.selectSet(defaultSet, true); // true = skip variant update
            }
          }
        } else {
          // No current variant found, select default and update variant
          const defaultSet = this.getDefaultSet();
          if (defaultSet) {
            this.selectSet(defaultSet, false); // false = update variant
          }
        }
      }
    },
    
    /**
     * Get current variant from theme data
     * @return {Object|null} - Current variant or null
     */
    getCurrentVariantFromTheme: function() {
      const selectElement = DOMHelper.getElement('select[id^="Option-"][name^="options["]');
      if (selectElement && selectElement.selectedIndex >= 0) {
        const opt = selectElement.options[selectElement.selectedIndex];
        if (opt && (opt.dataset.variantId || opt.dataset.variantSku)) {
          return {
            id: opt.dataset.variantId ? Number(opt.dataset.variantId) : null,
            sku: opt.dataset.variantSku || null,
            title: opt.textContent.trim()
          };
        }
      }
      const idInput = DOMHelper.getElement('input[name="id"]');
      if (idInput && idInput.value) {
        return { id: Number(idInput.value) };
      }
      return null;
    },
    
    initFromUrl: function() {
      const variantId = this.getUrlParameter('variant');
      if (!variantId) return false;
      const root = getScopeRoot();
      let sku = '';
      try {
        const opt = root.querySelector(`select[id^="Option-"][name^="options["] option[data-variant-id="${CSS.escape(String(variantId))}"]`);
        sku = (opt && opt.dataset && opt.dataset.variantSku ? String(opt.dataset.variantSku).trim() : '');
      } catch (_e) {
        // ignore
      }
      if (!sku) return false;
      
      // Find the matching fixed set
      const matchingSet = Config.fixedSets.find(set => set.sku === sku);
      if (!matchingSet) return false;
      
      this.state.selectedSetId = matchingSet.id;
      
      return true;
    },
    
    /**
     * Update UI components
     * @param {boolean} updateVariant - Whether to update the variant selection
     */
    updateUI: function(updateVariant = true) {
      // Update visual selection based on current state
      const currentSet = this.getSets().find(set => set.id === this.state.selectedSetId);
      if (currentSet) {
        this.selectSet(currentSet, !updateVariant);
      }
    },
    
    getUrlParameter: function(name) {
      name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
      const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
      const results = regex.exec(location.search);
      return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    },
    
    /**
     * Get the default set (Meal-Prep)
     * @return {Object} - The default set object
     */
    getDefaultSet: function() {
      if (!Array.isArray(Config.fixedSets) || Config.fixedSets.length === 0) {
        return null;
      }
      const defaultSet = Config.fixedSets.find(set => set.id === Config.defaultSetId);
      return defaultSet || Config.fixedSets[0]; // Fallback to first set if default not found
    },
    
    createCarousel: function(container) {
      if (!container) return;
      
      // Create carousel container
      const carouselContainer = DOMHelper.createElement('div', {
        className: 'carousel-container'
      });
      
      // Create carousel track
      const carouselTrack = DOMHelper.createElement('div', {
        className: 'carousel-track',
        attributes: {
          'id': 'carousel-track'
        }
      });
      
      // Create set cards
      this.getSets().forEach((set, index) => {
        const setCard = this.createSetCard(set, index);
        carouselTrack.appendChild(setCard);
      });
      
      // Create navigation buttons
      const prevButton = DOMHelper.createElement('button', {
        className: 'carousel-nav carousel-prev',
        html: '<svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M15 18L9 12L15 6"/></svg>',
        attributes: {
          'type': 'button',
          'aria-label': Config.translations.get('previousSet')
        }
      });
      
      const nextButton = DOMHelper.createElement('button', {
        className: 'carousel-nav carousel-next',
        html: '<svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M9 18L15 12L9 6"/></svg>',
        attributes: {
          'type': 'button',
          'aria-label': Config.translations.get('nextSet')
        }
      });
      
      // Assemble carousel
      carouselContainer.appendChild(prevButton);
      carouselContainer.appendChild(carouselTrack);
      carouselContainer.appendChild(nextButton);
      
      // Add carousel to container
      container.appendChild(carouselContainer);
      
      // Create scroll indicators
      this.createScrollIndicators(container);

      // Set initial state of nav buttons
      this.updateNavButtons(container);
    },
    
    createSetCard: function(set, index) {
      const isSelected = this.state.selectedSetId === set.id;
      
      const setCard = DOMHelper.createElement('div', {
        className: `set-card ${isSelected ? 'selected' : ''}`,
        attributes: {
          'data-set-id': set.id,
          'data-set-index': index
        }
      });
      
      // Get translated content using FULLY DYNAMIC system
      const title = Config.translations.getSetName(set);
      const perfectFor = Config.translations.getSetDescription(set);
      
      const includes = Config.translations.buildIncludesList(set.includes);

      // Sold-out detection (no extra requests): options are already rendered as <option disabled> when unavailable.
      const isSoldOut = this.isSkuSoldOut(set.sku);

      // Visually mute sold-out cards, but keep them clickable.
      if (isSoldOut) {
        setCard.classList.add('is-soldout');
      }
      
      // Create image container
      const imageContainer = DOMHelper.createElement('div', {
        className: 'set-image-container media-wrapper'
      });
      
      // Add set image
      const setImage = DOMHelper.createElement('img', {
        className: 'set-image',
        is: 'lazy-image',
        attributes: {
          'src': Config.assets.getSetImage(set),
          'alt': title,
          'loading': 'lazy',
          'decoding': 'async'
        }
      });
      
      imageContainer.setAttribute('loading', 'true');
      const markLoaded = () => imageContainer.removeAttribute('loading');
      if (setImage.complete) {
        markLoaded();
      } else {
        setImage.addEventListener('load', () => (window.requestIdleCallback ? window.requestIdleCallback(markLoaded, { timeout: 150 }) : setTimeout(markLoaded)), { once: true });
        setImage.addEventListener('error', markLoaded, { once: true });
      }
      
      imageContainer.appendChild(setImage);
      
      // Add badge using simplified system
      let badgeElement = null;
      
      if (isSoldOut) {
        const soldOutText = Config.translations.get('sold_out') || 'Sold out';
        badgeElement = DOMHelper.createElement('div', {
          className: 'badge badge-soldout',
          text: soldOutText
        });
      } else if (set.popular) {
        badgeElement = DOMHelper.createElement('div', {
          className: 'badge popular-badge',
          text: Config.translations.get('popular') || 'Popular'
        });
      } else if (set.badgeType) {
        // Always create a badge if badgeType exists
        const badgeText = Config.translations.get(`badge_${set.badgeType}`) || set.badgeType.replace('_', ' ');
        badgeElement = DOMHelper.createElement('div', {
          className: `badge badge-${set.badgeType}`,
          text: badgeText
        });
      }
      
      if (badgeElement) {
        imageContainer.appendChild(badgeElement);
      }
      
      // Create content container
      const contentContainer = DOMHelper.createElement('div', {
        className: 'set-content'
      });
      
      // Add title
      const titleElement = DOMHelper.createElement('h3', {
        className: 'set-title',
        text: title
      });
      
      // Add includes list with new design
      const includesList = DOMHelper.createElement('div', {
        className: 'set-includes',
        html: includes.map(item => `
          <div class="include-item">
            <svg class="include-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            <span>${item}</span>
          </div>`).join('')
      });
      
      // Add perfect for (will be hidden on mobile via CSS)
      const perfectForElement = DOMHelper.createElement('div', {
        className: 'set-perfect-for',
        html: `<strong>${Config.translations.get('perfect_for')}:</strong> ${perfectFor}`
      });
      

      // Bottom container to pin availability + perfect-for + price to the bottom
      const bottomContainer = DOMHelper.createElement('div', {
        className: 'set-bottom'
      });
      
      // Assemble content
      contentContainer.appendChild(titleElement);
      contentContainer.appendChild(includesList);
      // Availability notes intentionally removed for carousel
      bottomContainer.appendChild(perfectForElement);
      contentContainer.appendChild(bottomContainer);
      
      // Assemble card
      setCard.appendChild(imageContainer);
      setCard.appendChild(contentContainer);
      
      return setCard;
    },
    
    
    createScrollIndicators: function(container) {
      const indicatorsContainer = DOMHelper.createElement('div', {
        className: 'carousel-indicators'
      });
      
      // Add indicators for fixed sets
      this.getSets().forEach((set, index) => {
        const indicator = DOMHelper.createElement('button', {
          className: `indicator ${this.state.selectedSetId === set.id ? 'active' : ''}`,
          attributes: {
            'type': 'button',
            'data-set-index': index,
            'aria-label': `${Config.translations.get('selectSet')} ${index + 1}`
          }
        });
        
        indicatorsContainer.appendChild(indicator);
      });
      
      container.appendChild(indicatorsContainer);
    },
    
    selectSet: function(set, skipVariantUpdate = false) {
      if (!set) return;
      this.state.selectedSetId = set.id;

      const { container } = this.getContext();
      if (!container) return;
      
      // Update visual selection
      container.querySelectorAll('.set-card').forEach(card => {
        card.classList.remove('selected');
      });
      
      const selectedCard = container.querySelector(`[data-set-id="${set.id}"]`);
      if (selectedCard) {
        selectedCard.classList.add('selected');
        
        // Scroll card into view
        this.scrollToCard(selectedCard, container);
      }
      
      // Update indicators
      const sets = this.getSets();
      container.querySelectorAll('.indicator').forEach((indicator, index) => {
        indicator.classList.remove('active');
        // Only update indicators for actual fixed sets (not the custom indicator)
        if (index < sets.length && sets[index].id === set.id) {
          indicator.classList.add('active');
        }
      });
      
      // Update variant unless explicitly skipped
      if (!skipVariantUpdate) {
        if (window.VariantManager && typeof window.VariantManager.updateVariantBySku === 'function') {
          window.VariantManager.updateVariantBySku(set.sku);
        }
      }
    },
    
    /**
     * Select set by SKU without updating variant
     * @param {string} sku - SKU of the set to select
     * @param {boolean} skipVariantUpdate - Whether to skip variant update
     */
    selectSetBySku: function(sku, skipVariantUpdate = false) {
      const matchingSet = this.getSets().find(set => set.sku === sku);
      if (matchingSet) {
        this.selectSet(matchingSet, skipVariantUpdate);
      } else {
        console.warn(`[Carousel] No fixed set found for SKU: ${sku}`);
      }
    },
    
    scrollToCard: function(card, container) {
      const scopeContainer = container || (this.getContext().container);
      const track = scopeContainer ? scopeContainer.querySelector('#carousel-track') : null;
      if (!track || !card) return;
      
      const cardRect = card.getBoundingClientRect();
      const trackRect = track.getBoundingClientRect();
      const scrollLeft = card.offsetLeft - (trackRect.width / 2) + (cardRect.width / 2);
      
      track.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    },
    
    setupEventListeners: function(container) {
      const scopeContainer = container || (this.getContext().container);
      if (!scopeContainer) return;

      // Set card clicks
      scopeContainer.addEventListener('click', (e) => {
        const setCard = e.target.closest('.set-card');
        if (setCard) {
          const setId = setCard.dataset.setId;
          
          // Skip if this is the custom set card (handled by its own click handler)
          if (setId === 'custom') {
            return;
          }
          
          const set = Config.fixedSets.find(s => s.id === setId);
          if (set) {
            // Add visual feedback
            setCard.classList.add('just-selected');
            setTimeout(() => setCard.classList.remove('just-selected'), 400);
            
            this.selectSet(set);
          }
        }
      });
      
      // Navigation buttons
      const prevButton = scopeContainer.querySelector('.carousel-prev');
      const nextButton = scopeContainer.querySelector('.carousel-next');
      
      if (prevButton) {
        prevButton.addEventListener('click', () => {
          this.navigateCarousel(-1);
        });
      }
      
      if (nextButton) {
        nextButton.addEventListener('click', () => {
          this.navigateCarousel(1);
        });
      }
      
      // Scroll event for the track
      const track = scopeContainer.querySelector('#carousel-track');
      if (track) {
        track.addEventListener('scroll', () => {
          this.updateNavButtons(scopeContainer);
        });
      }

      // Indicator clicks
      scopeContainer.addEventListener('click', (e) => {
        const indicator = e.target.closest('.indicator');
        if (indicator) {
          const index = parseInt(indicator.dataset.setIndex);
          const sets = this.getSets();
          if (index < sets.length) {
            // It's a fixed set indicator
            const set = sets[index];
            if (set) {
              this.selectSet(set);
            }
          }
        }
      });
    },
    
    navigateCarousel: function(direction) {
      const sets = this.getSets();
      const lastSetIndex = sets.length - 1;
      let currentIndex = sets.findIndex(set => set.id === this.state.selectedSetId);

      if (currentIndex === -1) currentIndex = 0; // Default to first item if none selected

      const newIndex = currentIndex + direction;

      // Ensure newIndex is within the bounds of all cards
      if (newIndex >= 0 && newIndex <= lastSetIndex) {
        this.selectSet(sets[newIndex]);
      }
    },

    updateNavButtons: function(container) {
      const scopeContainer = container || (this.getContext().container);
      if (!scopeContainer) return;

      const track = scopeContainer.querySelector('#carousel-track');
      const prevButton = scopeContainer.querySelector('.carousel-prev');
      const nextButton = scopeContainer.querySelector('.carousel-next');

      if (!track || !prevButton || !nextButton) return;

      const scrollLeft = track.scrollLeft;
      const scrollWidth = track.scrollWidth;
      const clientWidth = track.clientWidth;

      // Disable prev button at the start
      prevButton.disabled = scrollLeft < 1;

      // Disable next button at the end (with a 1px tolerance)
      nextButton.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
    }
  };
  
  // Expose globally for merged picker access
  window.EcoSealCarouselPicker = EcoSealCarouselPicker;
  
  // Also expose the Config for merged picker access
  EcoSealCarouselPicker.Config = Config;
  
  // Initialize the application only if not in merged mode
  document.addEventListener('DOMContentLoaded', function() {
    const root = getScopeRoot();
    // Only auto-initialize if we're not in a merged picker (standalone mode)
    if (!DOMHelper.getElement('.ecoseal-merged-picker', root)) {
      EcoSealCarouselPicker.init();
    }
  });

  // Fallback initialization for dynamic content
  (function() {
    const initializeCarousel = function() {
      const root = getScopeRoot();
      if (!DOMHelper.getElement('.ecoseal-carousel-picker', root)) return;
      // Only auto-initialize if we're not in a merged picker (standalone mode)
      if (!DOMHelper.getElement('.ecoseal-merged-picker', root)) {
        EcoSealCarouselPicker.init();
      }
    };

    if (document.readyState !== 'loading') {
      initializeCarousel();
    }

    document.addEventListener('quickview:loaded', initializeCarousel);
  })();
})(); 